import * as z from "zod/v4";

// Gorgias Customer object
// https://developers.gorgias.com/reference/the-customer-object
export const gorgiasCustomerSchema = z.object({
  id: z.number().int(),
  email: z.nullable(z.string()),
  name: z.nullable(z.string()),
  firstname: z.string(),
  lastname: z.string(),
  external_id: z.nullable(z.string()).optional(),
  language: z.nullable(z.string()).optional(),
  note: z.nullable(z.string()).optional(),
  timezone: z.nullable(z.string()).optional(),
  created_datetime: z.nullable(z.string()),
  updated_datetime: z.nullable(z.string()),
});

export type GorgiasCustomer = z.infer<typeof gorgiasCustomerSchema>;

// Gorgias Ticket object
// https://developers.gorgias.com/reference/the-ticket-object
export const gorgiasTicketCustomerSchema = z.object({
  id: z.number().int(),
  email: z.nullable(z.string()).optional(),
  name: z.nullable(z.string()).optional(),
  firstname: z.string().optional(),
  lastname: z.string().optional(),
});

export const gorgiasTicketSchema = z.object({
  id: z.number().int(),
  uri: z.string().optional(),
  external_id: z.nullable(z.string()).optional(),
  status: z.string(),
  channel: z.nullable(z.string()),
  via: z.nullable(z.string()).optional(),
  from_agent: z.nullable(z.boolean()).optional(),
  spam: z.boolean().optional(),
  customer: z.nullable(gorgiasTicketCustomerSchema).optional(),
  subject: z.nullable(z.string()),
  language: z.nullable(z.string()).optional(),
  tags: z.array(z.object({ name: z.string() })).optional(),
  messages_count: z.number().int().optional(),
  excerpt: z.nullable(z.string()).optional(),
  created_datetime: z.string(),
  opened_datetime: z.nullable(z.string()).optional(),
  last_received_message_datetime: z.nullable(z.string()).optional(),
  last_message_datetime: z.nullable(z.string()).optional(),
  updated_datetime: z.nullable(z.string()).optional(),
  closed_datetime: z.nullable(z.string()).optional(),
  trashed_datetime: z.nullable(z.string()).optional(),
  snooze_datetime: z.nullable(z.string()).optional(),
  is_unread: z.boolean().optional(),
});

export type GorgiasTicket = z.infer<typeof gorgiasTicketSchema>;

// Gorgias TicketMessage object
// https://developers.gorgias.com/reference/the-ticketmessage-object
const gorgiasMessageSenderSchema = z.object({
  id: z.number().int().optional(),
  email: z.nullable(z.string()).optional(),
  name: z.nullable(z.string()).optional(),
});

export const gorgiasMessageSchema = z.object({
  id: z.number().int(),
  uri: z.string().optional(),
  message_id: z.nullable(z.string()).optional(),
  ticket_id: z.number().int().optional(),
  external_id: z.nullable(z.string()).optional(),
  public: z.boolean().optional(),
  channel: z.nullable(z.string()).optional(),
  via: z.nullable(z.string()).optional(),
  sender: z.nullable(gorgiasMessageSenderSchema).optional(),
  integration_id: z.nullable(z.number().int()).optional(),
  from_agent: z.boolean(),
  subject: z.nullable(z.string()).optional(),
  body_text: z.nullable(z.string()),
  body_html: z.nullable(z.string()).optional(),
  stripped_text: z.nullable(z.string()).optional(),
  stripped_html: z.nullable(z.string()).optional(),
  created_datetime: z.string(),
  sent_datetime: z.nullable(z.string()).optional(),
  opened_datetime: z.nullable(z.string()).optional(),
  deleted_datetime: z.nullable(z.string()).optional(),
  failed_datetime: z.nullable(z.string()).optional(),
});

export type GorgiasMessage = z.infer<typeof gorgiasMessageSchema>;

// Gorgias paginated list response wrapper
export const gorgiasListResponseSchema = <T extends z.ZodType>(itemSchema: T) =>
  z.object({ data: z.array(itemSchema) });

// Gorgias renders `{{…}}` form templates before POSTing; an unresolved template
// arrives as the literal `{{…}}` string or "", both meaning absent.
const rendered = (raw: unknown): string | undefined =>
  typeof raw === "string" && raw.length > 0 && !raw.startsWith("{{")
    ? raw
    : undefined;

export const GORGIAS_REDO_SUPPORT_TICKET_ID_META_KEY = "redo_support_ticket_id";

// Ticket-status webhook payload, normalized on ingest.
export const gorgiasTicketStatusWebhookSchema = z.object({
  ticketId: z.preprocess(rendered, z.string()),
  status: z.preprocess(rendered, z.string()),
  snoozeDatetime: z.preprocess(rendered, z.string().optional()),
  redoSupportTicketId: z.preprocess(rendered, z.string().optional()),
});

export type GorgiasTicketStatusWebhook = z.infer<
  typeof gorgiasTicketStatusWebhookSchema
>;

interface GorgiasMessageParty {
  id?: number;
  email?: string;
  name?: string;
}

interface GorgiasMessageSource {
  type: string;
  from?: { name?: string; address: string };
  to?: { name?: string; address: string }[];
}

interface GorgiasCreateMessage {
  channel: string;
  via?: string;
  from_agent: boolean;
  body_text: string;
  sender?: GorgiasMessageParty;
  receiver?: GorgiasMessageParty;
  source?: GorgiasMessageSource;
}

export interface GorgiasCreateTicketRequest {
  customer?: { id: number };
  channel?: string;
  via?: string;
  subject?: string;
  status?: "open" | "closed";
  meta?: Record<string, unknown>;
  messages: GorgiasCreateMessage[];
}

// Ticket summary used in dispute detail UI and evidence generation
export const gorgiasTicketSummarySchema = z.object({
  id: z.number().int(),
  subject: z.string(),
  status: z.string(),
  createdAt: z.string(),
  snippet: z.string(),
  url: z.string(),
});

export type GorgiasTicketSummary = z.infer<typeof gorgiasTicketSummarySchema>;
